import { Button, Card, CardContent, CardHeader, Input } from '@ibsys/kit';
import { SaveIcon } from 'lucide-react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  useCompetencyTree,
  useUpdateCompetency,
  useDeleteCompetency,
  useLinkRules,
  type CompetencyResponse,
} from '@/entities/coaching';
import { ConfirmationButton } from '@/shared/ui';
import type { RuleWithPath } from '../AddRulesDialog/types';
import { RulesSection } from './RulesSection';

type CompetencyCardProps = {
  competencyId: number;
  onDeleted: () => void;
};

/** Рекурсивно находит компетенцию в дереве по ID */
const findCompetency = (competencies: CompetencyResponse[], id: number): CompetencyResponse | undefined => {
  for (const c of competencies) {
    if (c.id === id) return c;
    const found = findCompetency(c.children, id);
    if (found) return found;
  }
  return undefined;
};

export const CompetencyCard: FC<CompetencyCardProps> = ({ competencyId, onDeleted }) => {
  const { t } = useTranslation(['common', 'coaching']);
  const { data: treeData, isLoading } = useCompetencyTree();
  const { mutateAsync: updateCompetency, isPending: isUpdating } = useUpdateCompetency();
  const { mutateAsync: deleteCompetency, isPending: isDeleting } = useDeleteCompetency();
  const { mutateAsync: linkRulesMutation, isPending: isLinking } = useLinkRules();

  const [name, setName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [localRules, setLocalRules] = useState<RuleWithPath[]>([]);
  const [rulesChanged, setRulesChanged] = useState(false);

  const competency = useMemo(() => {
    if (!treeData?.competencies) return undefined;
    return findCompetency(treeData.competencies, competencyId);
  }, [treeData?.competencies, competencyId]);

  const hasParent = competency?.parentCompetencyId !== null;

  // Синхронизация состояния при смене компетенции
  useEffect(() => {
    if (competency) {
      setName(competency.name);
      setLocalRules(competency.rules ?? []);
      setHasChanges(false);
      setRulesChanged(false);
    }
  }, [competency]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setHasChanges(e.target.value !== competency?.name);
  };

  const handleSave = async () => {
    if (!competency || !name.trim()) return;

    if (hasChanges) {
      await updateCompetency({
        id: competencyId,
        body: {
          name: name.trim(),
          parentCompetencyId: competency.parentCompetencyId,
          orderPosition: competency.orderPosition,
        },
      });
    }

    if (rulesChanged && hasParent) {
      await linkRulesMutation({
        id: competencyId,
        body: { ruleIds: localRules.map((r) => r.id) },
      });
    }

    setHasChanges(false);
    setRulesChanged(false);
  };

  const handleDelete = async () => {
    await deleteCompetency(competencyId);
    onDeleted();
  };

  const handleAddRules = useCallback((rules: RuleWithPath[]) => {
    setLocalRules((prev) => [...prev, ...rules]);
    setRulesChanged(true);
  }, []);

  const handleRemoveRule = useCallback((ruleId: number) => {
    setLocalRules((prev) => prev.filter((r) => r.id !== ruleId));
    setRulesChanged(true);
  }, []);

  const handleNavigateToReport = useCallback((expertReportId: number, blockId: number) => {
    window.open(`/expert-report/${expertReportId}/block/${blockId}`, '_blank');
  }, []);

  if (isLoading) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('common:loading')}</div>
        </CardHeader>
      </Card>
    );
  }

  if (!competency) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('coaching:selectItem')}</div>
        </CardHeader>
      </Card>
    );
  }

  const showSave = hasChanges || rulesChanged;

  return (
    <Card className="w-full h-fit">
      <CardHeader>
        <div className="space-y-4">
          <Input id="competency-name" label={t('coaching:competencyName')} value={name} onChange={handleNameChange} />
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Секция привязанных правил (только для дочерних компетенций) */}
        {hasParent && (
          <RulesSection
            rules={localRules}
            onRemoveRule={handleRemoveRule}
            onAddRules={handleAddRules}
            onNavigateToReport={handleNavigateToReport}
          />
        )}

        <div className="flex items-center gap-2">
          {showSave && (
            <Button
              variant="default"
              onClick={handleSave}
              disabled={isUpdating || isLinking || !name.trim()}
              icon={<SaveIcon className="size-4" />}
            >
              {t('common:save')}
            </Button>
          )}

          <ConfirmationButton
            onConfirm={handleDelete}
            isLoadingConfirm={isDeleting}
            title={t('coaching:removeCompetency')}
            description={
              <Trans
                i18nKey="coaching:removeCompetencyDescription"
                values={{ competencyName: competency.name }}
                components={{ strong: <strong /> }}
              />
            }
            trigger={{
              variant: 'outline',
              color: 'destructive',
              children: t('coaching:deleteCompetency'),
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
};
